// ————————————————————————————————————————————————————————————————
// Shared globals
// ————————————————————————————————————————————————————————————————
const getRoot = () => window.Shopify?.routes?.root || '/';
const root = getRoot();
const cartJsUrl = `${root}cart.js`;
const cartAddUrl = `${root}cart/add.js`;
const cartChangeUrl = `${root}cart/change.js`;
const cartDrawerSectionUrl = `${root}?section_id=cart-drawer`;
let __drawerOpenLock = false;

const CART_DRAWER_OPEN_PARAM = 'open_cart_drawer';

function isCartPath(pathname) {
  const rootPath = new URL(root, window.location.origin).pathname.replace(/\/$/, '');
  const normalizedPath = pathname.replace(/\/$/, '');
  return normalizedPath === `${rootPath}/cart` || normalizedPath === '/cart';
}

function normalizeInternalUrl(url) {
  try {
    return new URL(url, window.location.origin);
  } catch (err) {
    return null;
  }
}

function buildDrawerFallbackUrl() {
  const url = new URL(root, window.location.origin);
  const current = new URL(window.location.href);

  current.searchParams.forEach((value, key) => {
    if (!url.searchParams.has(key)) url.searchParams.set(key, value);
  });

  url.searchParams.set(CART_DRAWER_OPEN_PARAM, '1');
  return url.toString();
}

// ————————————————————————————————————————————————————————————————
// Monk popup close helper
// ————————————————————————————————————————————————————————————————
function closeMonkPopup() {
  const dialog = document.querySelector('#mr-dialog, .mr-dialog');
  if (!dialog) return;

  const closeBtn = dialog.querySelector(
    '.mr-offer-header-con__icon-close, .mr-actions__skip-button'
  );

  if (closeBtn) {
    closeBtn.click();
  } else {
    dialog.classList.remove('mr-transition-in');
    dialog.remove();
    document.body.style.overflow = '';
  }
}

// ————————————————————————————————————————————————————————————————
// Fetch override (single source of truth for post-add)
// ————————————————————————————————————————————————————————————————
(function() {
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const [url] = args;
    const response = await originalFetch.apply(this, args);

    if (String(url).includes('cart/add.js') && response.ok) {
      try {
        const res = await fetch(cartJsUrl);
        const cart = await res.json();

        // Always open drawer after any successful add (Monk or custom)
        if (!__drawerOpenLock) {
          __drawerOpenLock = true;
          openCartDrawer();
          setTimeout(() => {
            __drawerOpenLock = false;
          }, 800);
        }

        updateCartItemCounts(cart.item_count);
        await updateCartDrawer(true);
        closeMonkPopup();
      } catch (err) {
        console.error('Post-add cart refresh failed:', err);
      }
    }
    return response;
  };
})();

// ————————————————————————————————————————————————————————————————
// XHR override (voor theme/Shopify add-to-cart die géén fetch gebruikt)
// ————————————————————————————————————————————————————————————————
(function() {
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this.__cartAddUrl = url;
    return origOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    this.addEventListener('load', async () => {
      try {
        if (this.__cartAddUrl && String(this.__cartAddUrl).includes('cart/add.js') && this.status >= 200 && this.status < 300) {
          const res = await fetch(cartJsUrl);
          const cart = await res.json();

          if (!__drawerOpenLock) {
            __drawerOpenLock = true;
            openCartDrawer();
            setTimeout(() => {
              __drawerOpenLock = false;
            }, 800);
          }

          updateCartItemCounts(cart.item_count);
          await updateCartDrawer(true);
          closeMonkPopup();
        }
      } catch (err) {
        console.error('XHR post-add refresh failed:', err);
      }
    });
    return origSend.apply(this, args);
  };
})();

// ————————————————————————————————————————————————————————————————
// Drawer open / close
// ————————————————————————————————————————————————————————————————
function openCartDrawer() {
  const cartDrawer = document.querySelector(".cart-drawer");
  if (cartDrawer) cartDrawer.classList.add("cart-drawer--active");
  document.body.classList.add("cart-drawer-open");
}

function closeCartDrawer() {
  const cartDrawer = document.querySelector(".cart-drawer");
  if (cartDrawer) cartDrawer.classList.remove("cart-drawer--active");
  document.body.classList.remove("cart-drawer-open");
}

// ————————————————————————————————————————————————————————————————
// Stable event delegation for close / prevent bubbling
// Run this ONCE on page load
// ————————————————————————————————————————————————————————————————
function setupDrawerCloseListeners() {
  // Click anywhere outside drawer → close
  document.addEventListener('click', (e) => {
    const drawer = document.querySelector('.cart-drawer');
    if (!drawer || !drawer.classList.contains('cart-drawer--active')) return;
    // If click is inside the box → ignore
    if (e.target.closest('.cart-drawer-box')) return;
    // Otherwise close
    closeCartDrawer();
  });

  // Close button
  document.addEventListener('click', (e) => {
    if (e.target.closest('.cart-drawer-header-right-close')) {
      closeCartDrawer();
    }
  });
}
setupDrawerCloseListeners();

// ————————————————————————————————————————————————————————————————
// Cart count & drawer refresh
// ————————————————————————————————————————————————————————————————
function updateCartItemCounts(count) {
  const itemCount = Number(count) || 0;

  document.querySelectorAll(".site-header-cart--count, [data-header-cart-count]").forEach(el => {
    if (itemCount > 0) {
      el.setAttribute("data-header-cart-count", itemCount);
      el.classList.add("visible");
    } else {
      el.setAttribute("data-header-cart-count", "");
      el.classList.remove("visible");
    }
  });
}

async function updateCartDrawer(immediateOnly = false) {
  const root = window.Shopify?.routes?.root || '/';
  const sectionUrl = `${root}?section_id=cart-drawer`;

  // Immediate
  try {
    const res = await fetch(sectionUrl);
    if (res.ok) {
      const text = await res.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const newContent = html.querySelector('.cart-drawer')?.innerHTML;
      if (newContent) {
        const drawer = document.querySelector('.cart-drawer');
        if (drawer) {
          drawer.innerHTML = newContent;
          addCartDrawerListeners(); // re-attach quantity etc.
        }
      }
    }
  } catch (err) {
    console.error('Immediate drawer update failed', err);
  }

  if (!immediateOnly) {
    setTimeout(async () => {
      try {
        const res = await fetch(sectionUrl);
        if (res.ok) {
          const text = await res.text();
          const html = document.createElement('div');
          html.innerHTML = text;
          const updated = html.querySelector('.cart-drawer')?.innerHTML;
          if (updated) {
            const drawer = document.querySelector('.cart-drawer');
            if (drawer) {
              drawer.innerHTML = updated;
              addCartDrawerListeners();
            }
          }
        }
      } catch (err) {
        console.warn('Follow-up drawer refresh failed', err);
      }
    }, 2400);
  }
}

// ————————————————————————————————————————————————————————————————
// Quantity changes
// ————————————————————————————————————————————————————————————————
async function handleCartUpdate(key, quantity) {
  const box = document.querySelector('.cart-drawer-box');
  if (box) box.classList.add('cart-drawer-box--loading');

  try {
    const root = window.Shopify?.routes?.root || '/';
    const res = await fetch(`${root}cart/change.js`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity })
    });

    if (!res.ok) throw new Error(`Change failed: ${res.status}`);
    const cart = await res.json();

    updateCartItemCounts(cart.item_count);
    await updateCartDrawer(true);
  } catch (err) {
    console.error('Cart update error:', err);
  } finally {
    if (box) box.classList.remove('cart-drawer-box--loading');
  }
}

function addCartDrawerListeners() {
  document.querySelectorAll(".cart-drawer-quantity-selector").forEach(selector => {
    const minus = selector.querySelector(".cart-drawer-quantity-selector-minus");
    const plus = selector.querySelector(".cart-drawer-quantity-selector-plus");
    const input = selector.querySelector("input");

    if (minus) {
      minus.onclick = async () => {
        const item = minus.closest('[data-line-item-key]');
        if (!item) return;
        const key = item.dataset.lineItemKey;
        const cur = Number(input.value);
        const next = Math.max(0, cur - 1);
        input.value = next;
        await handleCartUpdate(key, next);
      };
    }

    if (plus) {
      plus.onclick = async () => {
        const item = plus.closest('[data-line-item-key]');
        if (!item) return;
        const key = item.dataset.lineItemKey;
        const cur = Number(input.value);
        const next = cur + 1;
        input.value = next;
        await handleCartUpdate(key, next);
      };
    }

    if (input) {
      input.onchange = async () => {
        const item = input.closest('[data-line-item-key]');
        if (!item) return;
        const key = item.dataset.lineItemKey;
        const qty = Math.max(0, Number(input.value));
        await handleCartUpdate(key, qty);
      };
    }
  });
}

// ————————————————————————————————————————————————————————————————
// Add to cart interception (forms + buttons) [DEFENSIVE]
// ————————————————————————————————————————————————————————————————
const ATC_LOCK_MS = 1200;
const atcLocks = new Map();

function lock(key) {
  const now = Date.now();
  const last = atcLocks.get(key) || 0;
  if (now - last < ATC_LOCK_MS) return false;
  atcLocks.set(key, now);
  return true;
}

// Forms
document.addEventListener("submit", async (e) => {
  const form = e.target;
  if (!form || !form.matches('form[action*="/cart/add"]')) return;
  if (e.defaultPrevented) return;
  if (form.dataset.atcHandled === "1") return;

  e.preventDefault();
  e.stopImmediatePropagation();

  form.dataset.atcHandled = "1";
  setTimeout(() => { delete form.dataset.atcHandled; }, ATC_LOCK_MS);

  try {
    await fetch(`${root}cart/add.js`, { method: "POST", body: new FormData(form) });
    // drawer opening now handled by fetch/XHR override
  } catch (err) {
    console.error("Form add failed", err);
  }
});

// Quick add buttons
document.addEventListener("click", async (e) => {
  const btn = e.target.closest(".productitem--action-atc[data-quick-buy]");
  if (!btn || btn.disabled || btn.getAttribute("aria-disabled") === "true") return;
  if (e.defaultPrevented) return;

  const text = btn.querySelector(".atc-button--text")?.textContent?.trim();
  if (["Opties kiezen", "Choisir les options", "Select options"].includes(text)) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;
  if (!lock(`variant:${variantId}`)) return;

  e.preventDefault();
  e.stopImmediatePropagation();

  try {
    await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    // drawer opening now handled by fetch/XHR override
  } catch (err) {
    console.error("Quick add failed", err);
  }
});

// Product forms should never navigate to /cart when drawer mode is enabled
document.addEventListener('product-form:cart:navigate', (event) => {
  if (window.CART_REDIRECTION_ENABLED) return;

  event.preventDefault();
  openCartDrawer();
  updateCartDrawer(true);
});

// Any internal /cart link opens the drawer. Capture phase catches dynamic/theme links too.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href]');
  if (!link) return;

  const url = normalizeInternalUrl(link.getAttribute('href'));
  if (!url || url.origin !== window.location.origin || !isCartPath(url.pathname)) return;
  if (link.hasAttribute('data-allow-cart-page')) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  openCartDrawer();
  updateCartDrawer(true);
}, true);

// Last-resort fallback: if /cart still loads, bounce to storefront and open drawer.
if (!window.Shopify?.designMode && isCartPath(window.location.pathname)) {
  window.location.replace(buildDrawerFallbackUrl());
} else if (new URLSearchParams(window.location.search).get(CART_DRAWER_OPEN_PARAM) === '1') {
  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete(CART_DRAWER_OPEN_PARAM);
  window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
  window.addEventListener('load', () => {
    openCartDrawer();
    updateCartDrawer(true);
  });
}

// Initial
addCartDrawerListeners();
